import { pluralizeToString } from 'lib/string/pluralize'
import React, { useCallback, useContext, useMemo } from 'react'
import RadioInput from 'LuxKit/Radio/RadioInput'
import RadioGroup from 'LuxKit/Radio/RadioGroup'
import { rem } from 'polished'
import styled from 'styled-components'
import { GlobalSearchStateContext, GlobalSearchDispatchContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import { GlobalSearchStateActions } from 'contexts/GlobalSearch/GlobalSearchState'
import { normaliseOccupancies } from 'businessTraveller/utils/normaliseOccupancies'
import { connect } from 'react-redux'
import { selectSelectedTravellerEmployees } from 'businessTraveller/selectors/businessTravellerEmployeeSelectors'

const RoomRadioGroup = styled(RadioGroup)`
  padding-left: ${rem(16)};
  padding-right: ${rem(16)};
`

interface Props {
  size?: 'medium' | 'large'
}

interface MappedStateProps {
  travellerCount: number
}

function BusinessTravellerSelectHotelsRoomsContent({
  size = 'medium',
  travellerCount,
}: Props & MappedStateProps) {
  const globalSearchState = useContext(GlobalSearchStateContext)
  const globalSearchDispatch = useContext(GlobalSearchDispatchContext)
  const occupancies = useMemo(() => globalSearchState.occupancies ?? [], [globalSearchState.occupancies])

  // Default show 1 room to 4 rooms, adjust base on the traveller count
  // show from: traveller // 2 to traveller
  const roomOptions: Array<number> = useMemo(() => {
    const minRooms = Math.ceil(travellerCount / 2)
    const maxRooms = travellerCount
    if (maxRooms <= 4) return [1, 2, 3, 4]
    return Array.from({ length: maxRooms - minRooms + 1 }, (_, index) => index + minRooms)
  }, [travellerCount])

  const onChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const roomCount = Number(e.target.value)
    const newOccupancies = normaliseOccupancies(travellerCount, roomCount)
    globalSearchDispatch({ type: GlobalSearchStateActions.SET_OCCUPANCIES, occupancies: newOccupancies })
  }, [globalSearchDispatch, travellerCount])

  return (
    <RoomRadioGroup>
      {roomOptions.map((value) => (
        <RadioInput
          key={value}
          value={value.toString()}
          size={size}
          checked={occupancies.length === value}
          onChange={onChange}
          disabled={travellerCount > value * 2 || value > travellerCount}
        >
          {pluralizeToString('room', value)}
        </RadioInput>
      ))}
    </RoomRadioGroup>
  )
}

export default connect<MappedStateProps, {}, Props, App.State>((state) => {
  return {
    travellerCount: selectSelectedTravellerEmployees(state).length + state.businessTraveller.selectedTravellerGuestCount,
  }
})(BusinessTravellerSelectHotelsRoomsContent)
